import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { AuthContext } from "@/hooks/use-auth";
import { fetchSession, signOut as signOutRequest, startSsoFlow, type AdminSession } from "@/lib/auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "anonymous">("loading");

  const refresh = useCallback(async () => {
    try {
      const next = await fetchSession();
      setSession(next);
      setStatus(next ? "authenticated" : "anonymous");
    } catch {
      setSession(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(() => startSsoFlow(), []);

  const signOut = useCallback(async () => {
    await signOutRequest();
    setSession(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({ session, status, signIn, signOut, refresh }),
    [session, status, signIn, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
